import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Download, Linkedin, Facebook, Youtube, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackDownload } from '../utils/hubspot';

const Footer = () => {
  const [logoError, setLogoError] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Force check logo availability on mount
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      console.log('✅ Footer logo preload successful');
      setLogoLoaded(true);
      setLogoError(false);
    };
    img.onerror = () => {
      console.error('❌ Footer logo preload failed');
      setLogoError(true);
      setLogoLoaded(false);
    };
    img.src = '/ctc_logo_26_nav_-_footer.png';
  }, []);

  const handleLogoError = () => {
    const logoPath = '/ctc_logo_26_nav_-_footer.png';
    console.error('❌ Footer logo failed to load in img element');
    console.log(`Current URL: ${window.location.href}`);
    console.log(`Logo URL: ${window.location.origin}${logoPath}`);
    console.log(`User Agent: ${navigator.userAgent}`);
    setLogoError(true);
  };

  const handleLogoLoad = () => {
    console.log('✅ Footer logo loaded successfully in img element');
    setLogoError(false);
    setLogoLoaded(true);
  };

  const handleBrandingKitDownload = async () => {
    // Track download in HubSpot
    trackDownload('branding_kit', 'CTC-Waterworks-Complete-Branding-Kit.html');

    // Create the branding kit content
    const brandingKitHTML = createBrandingKitHTML();
    
    // Convert to downloadable file
    const blob = new Blob([brandingKitHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = 'CTC-Waterworks-Complete-Branding-Kit.html';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  };

  const createBrandingKitHTML = () => {
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>CTC Waterworks - Complete Branding Kit</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
            font-family: 'Arial', 'Helvetica', sans-serif; 
            line-height: 1.6; 
            color: #1e293b;
            background: #f8fafc;
        }
        .page { 
            max-width: 8.5in; 
            margin: 0 auto; 
            background: white; 
            padding: 1in; 
            box-shadow: 0 0 20px rgba(0,0,0,0.1);
            margin-bottom: 2rem;
        }
        .header { 
            background: linear-gradient(135deg, #0099ff, #0077cc); 
            color: white; 
            padding: 3rem 2rem; 
            text-align: center; 
            margin: -1in -1in 2rem -1in;
            border-radius: 0 0 20px 20px;
        }
        .header h1 { font-size: 3rem; font-weight: bold; margin-bottom: 0.5rem; letter-spacing: 2px; }
        .header h2 { font-size: 1.5rem; font-weight: normal; opacity: 0.9; }
        .header .version { font-size: 1rem; margin-top: 1rem; opacity: 0.8; }
        
        h1 { color: #0099ff; font-size: 2.5rem; margin-bottom: 1rem; }
        h2 { color: #1e293b; font-size: 2rem; margin: 2rem 0 1rem 0; border-bottom: 3px solid #0099ff; padding-bottom: 0.5rem; }
        h3 { color: #475569; font-size: 1.5rem; margin: 1.5rem 0 0.5rem 0; }
        
        .color-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin: 1rem 0; }
        .color-swatch { 
            text-align: center; 
            border: 1px solid #e2e8f0; 
            border-radius: 12px; 
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            transition: transform 0.2s;
        }
        .color-swatch:hover { transform: translateY(-2px); }
        .color-sample { height: 120px; }
        .color-info { padding: 1rem; background: white; }
        .color-name { font-weight: bold; margin-bottom: 0.5rem; font-size: 1.1rem; }
        .color-values { font-size: 0.9rem; color: #64748b; line-height: 1.4; }
        
        .typography-example { 
            border: 1px solid #e2e8f0; 
            padding: 2rem; 
            margin: 1rem 0; 
            border-radius: 12px;
            background: linear-gradient(135deg, #f8fafc, #f1f5f9);
        }
        
        .logo-guidelines { 
            display: grid; 
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); 
            gap: 1rem; 
            margin: 1rem 0; 
        }
        .logo-example { 
            border: 1px solid #e2e8f0; 
            padding: 1.5rem; 
            text-align: center; 
            border-radius: 12px;
            background: white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .usage-grid { 
            display: grid; 
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
            gap: 1rem; 
            margin: 1rem 0; 
        }
        .usage-item { 
            border: 1px solid #e2e8f0; 
            padding: 1.5rem; 
            border-radius: 12px;
            background: white;
            text-align: center;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            transition: transform 0.2s;
        }
        .usage-item:hover { transform: translateY(-2px); }
        
        .checklist { list-style: none; }
        .checklist li { 
            padding: 0.75rem 0; 
            border-bottom: 1px solid #f1f5f9;
            display: flex;
            align-items: center;
        }
        .checklist li:before { 
            content: "✓"; 
            color: #10b981; 
            font-weight: bold; 
            margin-right: 0.75rem; 
            font-size: 1.2rem;
            background: #dcfce7;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        }
        
        .footer { 
            background: linear-gradient(135deg, #1e293b, #0f172a); 
            color: white; 
            text-align: center; 
            padding: 2rem; 
            margin: 2rem -1in -1in -1in;
            border-radius: 20px 20px 0 0;
        }
        
        .highlight-box {
            background: linear-gradient(135deg, #dbeafe, #bfdbfe);
            border: 2px solid #3b82f6;
            border-radius: 12px;
            padding: 1.5rem;
            margin: 1rem 0;
        }
        
        .brand-values {
            background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
            border-radius: 12px;
            padding: 1.5rem;
            margin: 1rem 0;
        }
        
        @media print {
            .page { box-shadow: none; margin-bottom: 0; }
            body { background: white; }
        }
        
        @media (max-width: 768px) {
            .page { padding: 0.5in; }
            .header { margin: -0.5in -0.5in 1rem -0.5in; padding: 2rem 1rem; }
            .header h1 { font-size: 2rem; }
            .color-grid { grid-template-columns: 1fr; }
            .logo-guidelines { grid-template-columns: 1fr; }
            .usage-grid { grid-template-columns: 1fr; }
        }
    </style>
</head>
<body>

<!-- Cover Page -->
<div class="page">
    <div class="header">
        <h1>CTC WATERWORKS</h1>
        <h2>Complete Branding Kit & Guidelines</h2>
        <div class="version">Version 1.0 • 2024 • Professional Edition</div>
    </div>
    
    <div class="brand-values">
        <h2>Brand Overview</h2>
        <p style="font-size: 1.2rem; margin-bottom: 1rem;"><strong>Mission:</strong> Leading provider of agricultural water solutions, trusted by farmers nationwide for over 40 years.</p>
        
        <h3>Core Brand Values</h3>
        <ul class="checklist">
            <li>Innovation in agricultural technology and engineering</li>
            <li>Reliability and durability in harsh field conditions</li>
            <li>Customer-focused solutions and dedicated support</li>
            <li>Environmental stewardship and sustainable practices</li>
            <li>Quality craftsmanship and engineering excellence</li>
            <li>Deep agricultural expertise and industry knowledge</li>
        </ul>
    </div>
    
    <div class="highlight-box">
        <h3>Target Audience</h3>
        <p>Commercial farmers, large-scale agricultural operations, irrigation specialists, agricultural equipment dealers, and farming cooperatives across North America.</p>
        
        <h3 style="margin-top: 1rem;">Brand Personality</h3>
        <p>Professional yet approachable, reliable and trustworthy, innovative and forward-thinking, deeply rooted in agricultural expertise and farmer-focused solutions.</p>
    </div>
</div>

<!-- Logo Guidelines Page -->
<div class="page">
    <h2>Logo Guidelines & Usage</h2>
    
    <h3>Primary Logo Variations</h3>
    <div class="logo-guidelines">
        <div class="logo-example">
            <div style="background: #f8fafc; padding: 2rem; margin-bottom: 1rem; border-radius: 8px;">
                <div style="background: #0099ff; color: white; padding: 1rem; font-weight: bold; font-size: 1.5rem; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">CTC WATERWORKS</div>
            </div>
            <p><strong>Primary Logo</strong><br>Use on light backgrounds and white surfaces</p>
        </div>
        <div class="logo-example">
            <div style="background: #1e293b; padding: 2rem; margin-bottom: 1rem; border-radius: 8px;">
                <div style="background: white; color: #0099ff; padding: 1rem; font-weight: bold; font-size: 1.5rem; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">CTC WATERWORKS</div>
            </div>
            <p><strong>Reverse Logo</strong><br>Use on dark backgrounds and colored surfaces</p>
        </div>
        <div class="logo-example">
            <div style="background: #e5e7eb; padding: 2rem; margin-bottom: 1rem; border-radius: 8px;">
                <div style="color: #1e293b; padding: 1rem; font-weight: bold; font-size: 1.5rem; border: 2px solid #1e293b; border-radius: 6px;">CTC WATERWORKS</div>
            </div>
            <p><strong>Outline Version</strong><br>Use when color reproduction is limited</p>
        </div>
    </div>
    
    <div class="highlight-box">
        <h3>Critical Logo Usage Guidelines</h3>
        <ul class="checklist">
            <li>Maintain minimum clear space of 1x logo height on all sides</li>
            <li>Minimum size: 1 inch wide for print, 120px wide for digital</li>
            <li>Never stretch, skew, rotate, or distort the logo proportions</li>
            <li>Never change the official logo colors or add unauthorized colors</li>
            <li>Never add effects, shadows, outlines, or decorative elements</li>
            <li>Never place on busy backgrounds that reduce legibility</li>
            <li>Always use high-resolution files for professional applications</li>
        </ul>
    </div>
</div>

<!-- Color Palette Page -->
<div class="page">
    <h2>Official Color Palette</h2>
    
    <h3>Primary Brand Colors</h3>
    <div class="color-grid">
        <div class="color-swatch">
            <div class="color-sample" style="background: #0099ff;"></div>
            <div class="color-info">
                <div class="color-name">CTC Blue</div>
                <div class="color-values">
                    <strong>HEX:</strong> #0099ff<br>
                    <strong>RGB:</strong> 0, 153, 255<br>
                    <strong>CMYK:</strong> 100, 40, 0, 0<br>
                    <strong>Pantone:</strong> 2925 C
                </div>
            </div>
        </div>
        <div class="color-swatch">
            <div class="color-sample" style="background: #0077cc;"></div>
            <div class="color-info">
                <div class="color-name">CTC Blue Dark</div>
                <div class="color-values">
                    <strong>HEX:</strong> #0077cc<br>
                    <strong>RGB:</strong> 0, 119, 204<br>
                    <strong>CMYK:</strong> 100, 42, 0, 20<br>
                    <strong>Pantone:</strong> 3005 C
                </div>
            </div>
        </div>
    </div>
    
    <h3>Secondary Brand Colors</h3>
    <div class="color-grid">
        <div class="color-swatch">
            <div class="color-sample" style="background: #c0c0c0;"></div>
            <div class="color-info">
                <div class="color-name">CTC Silver</div>
                <div class="color-values">
                    <strong>HEX:</strong> #c0c0c0<br>
                    <strong>RGB:</strong> 192, 192, 192<br>
                    <strong>CMYK:</strong> 0, 0, 0, 25<br>
                    <strong>Pantone:</strong> Cool Gray 5 C
                </div>
            </div>
        </div>
        <div class="color-swatch">
            <div class="color-sample" style="background: #e8e8e8;"></div>
            <div class="color-info">
                <div class="color-name">CTC Silver Light</div>
                <div class="color-values">
                    <strong>HEX:</strong> #e8e8e8<br>
                    <strong>RGB:</strong> 232, 232, 232<br>
                    <strong>CMYK:</strong> 0, 0, 0, 9<br>
                    <strong>Pantone:</strong> Cool Gray 2 C
                </div>
            </div>
        </div>
    </div>
    
    <h3>Supporting Colors</h3>
    <div class="color-grid">
        <div class="color-swatch">
            <div class="color-sample" style="background: #1e293b;"></div>
            <div class="color-info">
                <div class="color-name">Charcoal</div>
                <div class="color-values">
                    <strong>HEX:</strong> #1e293b<br>
                    <strong>RGB:</strong> 30, 41, 59<br>
                    <strong>Usage:</strong> Text, accents
                </div>
            </div>
        </div>
        <div class="color-swatch">
            <div class="color-sample" style="background: #10b981;"></div>
            <div class="color-info">
                <div class="color-name">Success Green</div>
                <div class="color-values">
                    <strong>HEX:</strong> #10b981<br>
                    <strong>RGB:</strong> 16, 185, 129<br>
                    <strong>Usage:</strong> Positive actions
                </div>
            </div>
        </div>
    </div>
    
    <div class="highlight-box">
        <h3>Color Usage Guidelines</h3>
        <ul class="checklist">
            <li>CTC Blue is the primary brand color - use prominently</li>
            <li>Use CTC Blue Dark for depth and contrast</li>
            <li>Silver colors provide neutral balance and sophistication</li>
            <li>Maintain 4.5:1 contrast ratio for accessibility</li>
            <li>Test colors across different media and lighting conditions</li>
        </ul>
    </div>
</div>

<!-- Typography Page -->
<div class="page">
    <h2>Typography System</h2>
    
    <h3>Primary Typeface: Arial/Helvetica</h3>
    <p style="font-size: 1.1rem; margin-bottom: 1.5rem;">Clean, modern, and highly readable across all applications. Excellent for both digital and print materials with universal availability.</p>
    
    <div class="typography-example">
        <h1 style="font-size: 2.5rem; margin-bottom: 0.5rem; color: #0099ff;">Heading 1 - Arial Bold (40pt)</h1>
        <h2 style="font-size: 2rem; margin-bottom: 0.5rem; color: #1e293b;">Heading 2 - Arial Bold (32pt)</h2>
        <h3 style="font-size: 1.5rem; margin-bottom: 0.5rem; color: #475569;">Heading 3 - Arial Bold (24pt)</h3>
        <h4 style="font-size: 1.25rem; margin-bottom: 0.5rem; color: #64748b;">Heading 4 - Arial Bold (20pt)</h4>
        <p style="font-size: 1rem; margin-bottom: 0.5rem;"><strong>Body Bold - Arial Bold (16pt)</strong></p>
        <p style="font-size: 1rem; margin-bottom: 0.5rem;">Body Text - Arial Regular (16pt). This is the standard text used for paragraphs, descriptions, and general content throughout all CTC Waterworks materials. It provides excellent readability and professional appearance.</p>
        <p style="font-size: 0.875rem; color: #64748b;">Caption Text - Arial Regular (14pt). Used for image captions, footnotes, and supplementary information.</p>
    </div>
    
    <div class="highlight-box">
        <h3>Typography Hierarchy & Specifications</h3>
        <ul class="checklist">
            <li><strong>Headlines:</strong> Arial Bold, 32-48pt, CTC Blue or Charcoal</li>
            <li><strong>Subheadings:</strong> Arial Bold, 20-28pt, Charcoal or Gray</li>
            <li><strong>Body Text:</strong> Arial Regular, 14-16pt, Charcoal</li>
            <li><strong>Captions:</strong> Arial Regular, 12-14pt, Gray</li>
            <li><strong>Line Spacing:</strong> 1.4-1.6x font size for optimal readability</li>
            <li><strong>Paragraph Spacing:</strong> 1x line height between paragraphs</li>
        </ul>
    </div>
</div>

<!-- Visual Elements Page -->
<div class="page">
    <h2>Visual Elements & Photography</h2>
    
    <div class="brand-values">
        <h3>Photography Style Guidelines</h3>
        <ul class="checklist">
            <li>High-quality, professional agricultural imagery showcasing real operations</li>
            <li>Focus on CTC equipment in actual farming environments and applications</li>
            <li>Natural lighting preferred with bright, clear, well-exposed images</li>
            <li>Show products actively being used by real farmers and operators</li>
            <li>Include diverse crop types, farming operations, and geographic regions</li>
            <li>Emphasize the scale and professional nature of agricultural operations</li>
            <li>Avoid overly staged or artificial-looking compositions</li>
        </ul>
    </div>
    
    <div class="highlight-box">
        <h3>Iconography & Graphic Elements</h3>
        <ul class="checklist">
            <li>Simple, clean line icons with consistent 2-3px stroke width</li>
            <li>Use CTC Blue (#0099ff) as primary color for icons and graphics</li>
            <li>Maintain consistent sizing and spacing throughout applications</li>
            <li>Subtle gradients using brand colors for depth and visual interest</li>
            <li>Clean geometric shapes and minimal decorative elements</li>
            <li>Focus on functionality, clarity, and professional appearance</li>
        </ul>
    </div>
    
    <h3>Layout Principles</h3>
    <div class="typography-example">
        <p><strong>Grid System:</strong> Use consistent margins, padding, and alignment</p>
        <p><strong>White Space:</strong> Generous spacing for clean, professional appearance</p>
        <p><strong>Hierarchy:</strong> Clear visual hierarchy using size, color, and positioning</p>
        <p><strong>Balance:</strong> Balanced composition with proper weight distribution</p>
    </div>
</div>

<!-- Applications Page -->
<div class="page">
    <h2>Brand Applications & Usage</h2>
    
    <h3>Digital Applications</h3>
    <div class="usage-grid">
        <div class="usage-item">
            <h4 style="color: #0099ff; margin-bottom: 0.5rem;">Website</h4>
            <p>Primary logo in header, consistent color usage, Arial typography, responsive design</p>
        </div>
        <div class="usage-item">
            <h4 style="color: #0099ff; margin-bottom: 0.5rem;">Email Signatures</h4>
            <p>Logo max 200px wide, include complete contact information and website</p>
        </div>
        <div class="usage-item">
            <h4 style="color: #0099ff; margin-bottom: 0.5rem;">Social Media</h4>
            <p>Profile images, cover photos, consistent branding across all platforms</p>
        </div>
        <div class="usage-item">
            <h4 style="color: #0099ff; margin-bottom: 0.5rem;">Digital Advertising</h4>
            <p>Logo placement, brand colors, clear messaging, call-to-action buttons</p>
        </div>
    </div>
    
    <h3>Print Applications</h3>
    <div class="usage-grid">
        <div class="usage-item">
            <h4 style="color: #0099ff; margin-bottom: 0.5rem;">Business Cards</h4>
            <p>Standard 3.5" x 2", logo and complete contact information, professional finish</p>
        </div>
        <div class="usage-item">
            <h4 style="color: #0099ff; margin-bottom: 0.5rem;">Letterhead</h4>
            <p>8.5" x 11", logo in header, contact information in footer, clean layout</p>
        </div>
        <div class="usage-item">
            <h4 style="color: #0099ff; margin-bottom: 0.5rem;">Brochures</h4>
            <p>Consistent layout, brand colors, professional agricultural imagery</p>
        </div>
        <div class="usage-item">
            <h4 style="color: #0099ff; margin-bottom: 0.5rem;">Trade Shows</h4>
            <p>Banners, displays, promotional materials, booth graphics</p>
        </div>
    </div>
    
    <div class="highlight-box">
        <h3>Vehicle Graphics & Signage</h3>
        <ul class="checklist">
            <li>Logo prominently displayed on both sides of vehicles</li>
            <li>Contact information clearly visible and readable from distance</li>
            <li>Use high-contrast colors for maximum visibility</li>
            <li>Maintain professional appearance and brand consistency</li>
            <li>Include website URL and primary phone number</li>
        </ul>
    </div>
</div>

<!-- Final Page - Contact & Resources -->
<div class="page">
    <h2>Brand Assets & Resources</h2>
    
    <div class="brand-values">
        <h3>Available Digital Assets</h3>
        <ul class="checklist">
            <li>Logo files in PNG, SVG, EPS, and PDF formats</li>
            <li>Color palette swatches and specification files</li>
            <li>Typography specimens and font recommendations</li>
            <li>Professional photography library and image guidelines</li>
            <li>Icon library and graphic element templates</li>
            <li>Business card, letterhead, and presentation templates</li>
            <li>Social media templates and profile graphics</li>
        </ul>
    </div>
    
    <div class="highlight-box">
        <h3>File Format Guidelines</h3>
        <ul class="checklist">
            <li><strong>PNG:</strong> Web use, presentations, digital applications</li>
            <li><strong>SVG:</strong> Scalable web graphics, responsive design</li>
            <li><strong>EPS:</strong> Print production, professional printing</li>
            <li><strong>PDF:</strong> Documents, presentations, universal compatibility</li>
        </ul>
    </div>
    
    <h3>Brand Compliance & Quality Control</h3>
    <p style="margin-bottom: 1rem;">All marketing materials, communications, and brand applications should be reviewed for compliance with these guidelines. Consistent brand application builds trust, recognition, and professional credibility.</p>
    
    <div class="typography-example">
        <h3>Questions or Need Additional Assets?</h3>
        <p>For questions about brand usage, to request additional assets, or for brand compliance review, contact:</p>
        <p style="margin-top: 1rem;"><strong>CTC Waterworks Marketing Team</strong><br>
        <strong>Email:</strong> marketing@ctcwaterworks.com<br>
        <strong>Phone:</strong> (509) 901-7476<br>
        <strong>Address:</strong> 460 McLaughlin Rd, Yakima, WA 98908</p>
    </div>
    
    <div class="footer">
        <h3 style="color: white; margin-bottom: 1rem;">CTC WATERWORKS</h3>
        <p><strong>Leading Agricultural Water Solutions Since 1980</strong></p>
        <p style="margin-top: 1rem;">460 McLaughlin Rd, Yakima, WA 98908<br>
        Phone: (509) 901-7476 • Email: Sales@CTCWaterworks.com<br>
        Website: www.ctcwaterworks.com</p>
        <p style="margin-top: 1rem; font-size: 0.9rem; opacity: 0.8;">
            © 2024 CTC Waterworks. All rights reserved. This branding kit is proprietary and confidential.
        </p>
    </div>
</div>

</body>
</html>`;
  };

  const ContactItem = ({ icon: Icon, children, href, type }: { 
    icon: any, 
    children: React.ReactNode, 
    href?: string,
    type?: 'phone' | 'email' | 'address'
  }) => {
    const content = (
      <div className="flex items-center space-x-2">
        <Icon size={16} />
        {children}
      </div>
    );

    // Make phone numbers, emails, and addresses clickable
    if (href) {
      return (
        <a href={href} className="hover:text-ctc-blue-light transition-colors">
          {content}
        </a>
      );
    }

    return content;
  };

  return (
    <footer className="text-white relative overflow-hidden bg-gradient-to-br from-ctc-silver via-gray-600 to-gray-800">
      {/* Gradient overlay for enhanced depth */}
      <div className="absolute inset-0 bg-gradient-to-t from-gray-700/30 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
        {/* Social Media Icons - Centered at Top with reduced margins */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center space-x-6">
            <a
              href="https://www.linkedin.com/company/ctc-waterworks/?viewAsMember=true"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-ctc-blue/20 rounded-lg flex items-center justify-center hover:bg-ctc-blue hover:scale-110 transition-all duration-300 group"
              aria-label="Follow CTC Waterworks on LinkedIn"
            >
              <Linkedin className="text-ctc-blue group-hover:text-white" size={24} />
            </a>
            <a
              href="https://www.facebook.com/profile.php?viewas=100000686899395&id=61577642326281"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center hover:bg-blue-600 hover:scale-110 transition-all duration-300 group"
              aria-label="Follow CTC Waterworks on Facebook"
            >
              <Facebook className="text-blue-400 group-hover:text-white" size={24} />
            </a>
            <a
              href="https://www.youtube.com/@CTCWaterworks"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center hover:bg-red-600 hover:scale-110 transition-all duration-300 group"
              aria-label="Subscribe to CTC Waterworks on YouTube"
            >
              <Youtube className="text-red-400 group-hover:text-white" size={24} />
            </a>
            <a
              href="https://www.instagram.com/ctcwaterworks"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-pink-600/20 rounded-lg flex items-center justify-center hover:bg-pink-600 hover:scale-110 transition-all duration-300 group"
              aria-label="Follow CTC Waterworks on Instagram"
            >
              <Instagram className="text-pink-400 group-hover:text-white" size={24} />
            </a>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              {/* Make logo clickable to homepage */}
              <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
                {!logoError ? (
                  <img
                    src="/ctc_logo_26_nav_-_footer.png"
                    alt="CTC Waterworks"
                    className="h-15 w-auto"
                    onError={handleLogoError}
                    onLoad={handleLogoLoad}
                    style={{
                      maxWidth: '200px',
                      height: 'auto',
                      filter: 'brightness(1.1)'
                    }}
                  />
                ) : (
                  <div className="h-15 flex items-center">
                    <span className="text-2xl font-bold text-white tracking-tight bg-gradient-to-r from-ctc-blue to-ctc-blue-light bg-clip-text text-transparent">
                      CTC Waterworks
                    </span>
                  </div>
                )}
              </Link>
            </div>
            <p className="text-gray-300 mb-4 leading-relaxed text-sm">
              Better agricultural, municipal, industrial, and waste water.
              Trusted by farmers, engineers, and operators for over 40 years.
            </p>
            <div className="space-y-3 text-sm text-ctc-blue">
              <ContactItem
                icon={Phone}
                type="phone"
                href={isMobile ? "tel:+15099017476" : undefined}
              >
                <span>(509) 901-7476</span>
              </ContactItem>
              <ContactItem
                icon={Mail}
                type="email"
                href="mailto:Sales@CTCWaterworks.com"
              >
                <span>Sales@CTCWaterworks.com</span>
              </ContactItem>
              <ContactItem
                icon={MapPin}
                type="address"
                href="https://maps.google.com/?q=460+McLaughlin+Rd,+Yakima,+WA+98908"
              >
                <span>460 McLaughlin Rd.<br />Yakima, WA 98908</span>
              </ContactItem>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-ctc-blue">Products</h3>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li><Link to="/sulfur-burners" className="hover:text-ctc-blue transition-colors">Sulfur Burners</Link></li>
              <li><Link to="/hydrocoolers" className="hover:text-ctc-blue transition-colors">Hydrocoolers</Link></li>
              <li><Link to="/dpa-drenchers" className="hover:text-ctc-blue transition-colors">DPA Drenchers</Link></li>
              <li><Link to="/filtration" className="hover:text-ctc-blue transition-colors">Filtration</Link></li>
              <li className="ml-4"><Link to="/filtration/inline/big" className="hover:text-ctc-blue transition-colors text-sm">BIG Series</Link></li>
              <li className="ml-4"><Link to="/filtration/inline/big-s" className="hover:text-ctc-blue transition-colors text-sm">BIG-S (Stainless)</Link></li>
              <li className="ml-4"><Link to="/filtration/inline/gng" className="hover:text-ctc-blue transition-colors text-sm">GNG (Economy)</Link></li>
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-ctc-blue">Industries</h3>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li><Link to="/industries/agriculture" className="hover:text-ctc-blue transition-colors">Agriculture</Link></li>
              <li><Link to="/industries/municipal" className="hover:text-ctc-blue transition-colors">Municipal</Link></li>
              <li><Link to="/industries/industrial" className="hover:text-ctc-blue transition-colors">Industrial</Link></li>
              <li><Link to="/industries/hydropower" className="hover:text-ctc-blue transition-colors">Hydropower</Link></li>
              <li><Link to="/industries/mining" className="hover:text-ctc-blue transition-colors">Mining</Link></li>
              <li><Link to="/industries/aquaculture" className="hover:text-ctc-blue transition-colors">Aquaculture</Link></li>
              <li><Link to="/industries/environmental" className="hover:text-ctc-blue transition-colors">Environmental</Link></li>
              <li><Link to="/industries/desalination" className="hover:text-ctc-blue transition-colors">Desalination</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-ctc-blue">Resources</h3>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li><Link to="/flood-resilience" className="hover:text-ctc-blue transition-colors font-semibold text-red-400">Floods & Dirty Water Resource Hub</Link></li>
              <li><Link to="/sulfur-burner-guide" className="hover:text-ctc-blue transition-colors">Better Water Guide</Link></li>
              <li><Link to="/tools/acid-cost-calculator" className="hover:text-ctc-blue transition-colors">Acid Cost Calculator</Link></li>
              <li><Link to="/research" className="hover:text-ctc-blue transition-colors">Sulfur Burner Research</Link></li>
              <li><Link to="/filtration/parts" className="hover:text-ctc-blue transition-colors">Filtration Parts</Link></li>
              <li><Link to="/blog" className="hover:text-ctc-blue transition-colors">Blog</Link></li>
              <li><Link to="/contact" className="hover:text-ctc-blue transition-colors">Contact</Link></li>
              <li><Link to="/about" className="hover:text-ctc-blue transition-colors">About Us</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-ctc-blue">Company</h3>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li><Link to="/about" className="hover:text-ctc-blue transition-colors">About Us</Link></li>
              <li><Link to="/team" className="hover:text-ctc-blue transition-colors">Team</Link></li>
              <li><Link to="/contact" className="hover:text-ctc-blue transition-colors">Contact</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-500 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              <span>© 2026 CTC Waterworks. All rights reserved.</span>
            </div>
            <div className="flex items-center space-x-6 text-gray-400 text-sm">
              <Link to="/privacy-policy" className="hover:text-ctc-blue transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-ctc-blue transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;